import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  keyframes,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import mindIcon from '../../Assets/mind.png'; // adjust the path as needed
import sendIcon from '../../Assets/send.png';
import { useSelector } from "react-redux";
import { getUrl, getUserDisplayName } from "src/Utils/Common";
import ReactMarkdown from "react-markdown"; // At the top if not already

const ChatApp = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedQuestions, setSelectedQuestion] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const projectId = useSelector((state) => state.userProject.projectId);
  const [sessionId, setSessionId] = useState("");
  const [selectedOption, setSelectedOption] = useState('insights');
  const [newChat, setNewChat]= useState(true);
  
  // Responsive breakpoints
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const currentInputRef = useRef("");
  const blink = keyframes`
    0% { opacity: 0.2; }
    50% { opacity: 1; }
    100% { opacity: 0.2; }
    `;


  const handleSend = () => {
    if (input.trim() === "") return;
  
    const userMsg = { sender: "user", content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSuggestions([]);
    setLoading(true); // Start loading
  
    const startChat = async () => {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setLoading(false);
        return;
      }
  
      try {
        const response = await fetch(`${getUrl()}/market_overview/build-data-chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({
            user_id: getUserDisplayName(),
            project_id: projectId,
            message: input,
            end_chat: false,
            new_chat: newChat,
            chat_mode: selectedOption
          }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
  
        const result = await response.json();
        setSessionId(result.session_id);
        setNewChat(false)
        setLoading(false)
        if (result.options) {
          setSuggestions(result.options);
        }
        else{
            setSuggestions([]);
        }
  
        const queueMessages = [];
        if (result.message) queueMessages.push(result.message);
        if (result.insights_full) queueMessages.push(result.insights_full);
  
        const typeMarkdownMessage = (markdownText, index = 0) => {
          let i = 0;
          setMessages((prev) => [...prev, { sender: "bot", content: "", isMarkdown: true }]);
  
          typingIntervalRef.current = setInterval(() => {
            i++;
            setMessages((prevMessages) => {
              const updated = [...prevMessages];
              const lastIndex = updated.length - 1;
              updated[lastIndex] = {
                ...updated[lastIndex],
                content: markdownText.slice(0, i),
              };
              return updated;
            });
  
            if (i === markdownText.length) {
              clearInterval(typingIntervalRef.current);
              if (index + 1 < queueMessages.length) {
                setTimeout(() => {
                  typeMarkdownMessage(queueMessages[index + 1], index + 1);
                }, 100);
              } else {
                setLoading(false); // Done typing all responses
              }
            }
          }, 15);
        };
  
        if (queueMessages.length > 0) {
          setTimeout(() => {
            typeMarkdownMessage(queueMessages[0]);
          }, 100);
        } else {
          setLoading(false); // No bot message to type
        }
  
      } catch (error) {
        console.error("Error fetching chat response:", error);
        setLoading(false);
      }
    };
  
    startChat();
  };
  
  
  

  const handleSuggestionClick = (suggestion) => {
    // Clear existing interval if any
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
  
    // Reset input
    setInput("");
    currentInputRef.current = "";
  
    let i = 0;
  
    typingIntervalRef.current = setInterval(() => {
      if (i < suggestion.length) {
        currentInputRef.current += suggestion[i];
        setInput(currentInputRef.current);
        i += 1;
      } else {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
  
        // Optionally auto-send after typing
        setSelectedQuestion(suggestion);
      }
    }, 50);

    
  };

  useEffect(()=>{
    const startChat = async () => {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setLoading(false);
        return;
      }
  
      try {
        const response = await fetch(`${getUrl()}/market_overview/build-data-chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({
            user_id: getUserDisplayName(),
            project_id: projectId,
            message: "end",
            end_chat: true,
          }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
  
        const result = await response.json();
      } catch (error) {
        console.error("Error fetching chat response:", error);
        setLoading(false);
      }
    };
  
    startChat();
  },[])

  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);


  useEffect(()=>{
    handleSend();
    setInput("");
  },[selectedQuestions])

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages]);

  return (
    <Box
      sx={{
        height: { xs: "calc(100vh - 140px)", sm: "calc(100vh - 160px)", md: "600px" },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
        maxHeight: { xs: "none", md: "600px" },
      }}
    >
      {/* Messages List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: { xs: 1, sm: 1.5, md: 2 },
          backgroundColor: "#f9f9f9",
        }}
      >
        {messages.map((msg, index) => (
            <Box
                key={index}
                display="flex"
                justifyContent={msg.sender === "user" ? "flex-end" : "flex-start"}
                mb={1}
                sx={{ 
                  marginLeft: { xs: "4px", sm: "8px", md: "10px" }, 
                  marginRight: { xs: "4px", sm: "8px", md: "10px" } 
                }}
            >
                <Paper
                sx={{
                    padding: { xs: "6px 10px", sm: "7px 11px", md: "8px 12px" },
                    backgroundColor: msg.sender === "user" ? "#00265E" : "transparent",
                    color: msg.sender === "user" ? "white" : "black",
                    maxWidth: { xs: "85%", sm: "80%", md: "75%" },
                    wordBreak: "break-word",
                    borderRadius: "20px",
                }}
                >
                {msg.isMarkdown ? (
                    <div style={{
                      padding: isMobile ? "6px" : "10px",
                      fontSize: isMobile ? "14px" : "16px",
                    }}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    
                ) : (
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem", md: "0.875rem" } }}
                    >
                      {msg.content}
                    </Typography>
                )}
                </Paper>
            </Box>
            ))}
            {loading && (
            <Box
                display="flex"
                justifyContent="flex-start"
                mb={1}
                sx={{ 
                  marginLeft: { xs: "4px", sm: "8px", md: "10px" }, 
                  marginRight: { xs: "4px", sm: "8px", md: "10px" } 
                }}
            >
                <Paper
                sx={{
                    padding: { xs: "6px 10px", sm: "7px 11px", md: "8px 12px" },
                    backgroundColor: "white",
                    color: "#555",
                    maxWidth: { xs: "85%", sm: "80%", md: "75%" },
                    borderRadius: "20px",
                    fontStyle: "italic",
                    alignItems: "center",
                }}
                >
                <Typography 
                  variant="body2"
                  sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem", md: "0.875rem" } }}
                >
                    MarketMind is thinking
                    <Box
                        component="span"
                        sx={{
                        display: "inline-block",
                        animation: `${blink} 1.2s infinite`,
                        }}
                    >
                        ...
                    </Box>
                </Typography>
                </Paper>
            </Box>
            )}
        <div ref={bottomRef} />
      </Box>

      {/* Input Bar */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        sx={{
          padding: { xs: 0.5, sm: 0.75, md: 1 },
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12}>
          {/* Suggested Questions */}
          {suggestions.length > 0 && (
          <Box
        sx={{
              padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" },
          width: "100%",
          display: "flex",
          overflowX: "auto",
              overflowY: "hidden",
              gap: { xs: 0.75, sm: 1, md: 2 },
          marginBottom: { xs: 1, sm: 1.5, md: 2 },
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
              scrollbarColor: "#ccc transparent",
          "&::-webkit-scrollbar": {
                height: { xs: "3px", sm: "4px", md: "4px" },
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "2px",
                "&:hover": {
                  backgroundColor: "#aaa",
                },
          },
        }}
      >
          {suggestions.map((suggestion, index) => (
          <Button
            key={index}
            variant="outlined"
            onClick={()=>handleSuggestionClick(suggestion)}
            sx={{
                borderRadius: { xs: "16px", sm: "18px", md: "20px" },
                padding: { xs: "4px 10px", sm: "6px 12px", md: "8px 16px" },
              backgroundColor: "white",
                minWidth: { xs: "auto", sm: "180px", md: "250px" },
                maxWidth: { xs: "85%", sm: "none", md: "none" },
                height: { xs: "auto", sm: "40px", md: "50px" },
                maxHeight: { xs: "none", sm: "45px", md: "50px" },
                fontSize: { xs: "0.7rem", sm: "0.8125rem", md: "0.875rem" },
                lineHeight: { xs: "1.3", sm: "1.4", md: "1.5" },
                whiteSpace: { xs: "normal", sm: "nowrap", md: "nowrap" },
                wordBreak: { xs: "break-word", sm: "normal", md: "normal" },
                textAlign: { xs: "left", sm: "center", md: "center" },
              flexShrink: 0,
                textTransform: "none",
                borderColor: "#00265E",
                color: "#00265E",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  borderColor: "#00265E",
                },
            }}
          >
            {suggestion}
          </Button>
        ))}
        </Box>
          )}
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                bgcolor: 'transparent',
                px: { xs: 0.5, sm: 1, md: 0 },
              }}
            >
              <TextField
                inputRef={inputRef}
                fullWidth
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                value={input}
                sx={{
                  maxWidth: { xs: '100%', sm: '90%', md: '800px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    '& fieldset': {
                      borderColor: 'white',
                    },
                    '&:hover fieldset': {
                      borderColor: '#BEBEBE',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00265E',
                    },
                    backgroundColor: 'white',
                  },
                }}
                onChange={(e) => setInput(e.target.value)}
                placeholder="What's on your mind?"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={mindIcon}
                        alt="icon"
                        style={{ 
                          width: isMobile ? 28 : isTablet ? 35 : 40, 
                          height: isMobile ? 28 : isTablet ? 35 : 40 
                        }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSend}
                        edge="end"
                        disabled={!input.trim()}
                        sx={{
                          padding: { xs: "6px", sm: "8px", md: "10px" },
                        }}
                      >
                        <img
                          src={sendIcon}
                          alt="icon"
                          style={{ 
                            width: isMobile ? 28 : isTablet ? 35 : 40, 
                            height: isMobile ? 28 : isTablet ? 35 : 40 
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Toggle text below the input */}
              <Box sx={{ 
                mt: { xs: 0.75, sm: 1, md: 1 }, 
                display: "flex", 
                gap: { xs: 2, sm: 2.5, md: 3 }
              }}>
                {["insights", "knowledge"].map((option) => (
                  <Typography
                    key={option}
                    variant="body1"
                    sx={{
                      cursor: "pointer",
                      fontWeight: selectedOption === option ? "bold" : "normal",
                      color: selectedOption === option ? "#00265E" : "gray",
                      textDecoration: "none",
                      fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "1rem" },
                    }}
                    onClick={() => setSelectedOption(option)}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Box>
    </Box>
  );
};

export default ChatApp;
